/**
 * run-color.ts — stable per-run hue assignment
 *
 * Assigns a deterministic low-saturation color from the Observatory CHART_COLORS
 * ramp (design ref §2.4) to a run ID. The hash is pure over runId so the same
 * run always gets the same hue across polls and reloads. Past the ramp length the
 * hue repeats by design — the mono run-id beside the dot disambiguates collisions.
 *
 * Pure, React-free, no side effects. Reused by the dot marker, runs view, and any
 * future legend without importing anything from the UI layer.
 */

/**
 * Observatory CHART_COLORS ramp — the seven low-saturation, theme-aware hues
 * reserved for data series (design ref §2.4). Never use these as UI accent,
 * fill, or feedback colors.
 */
const RUN_HUES = [
  '#79C9D3', // teal
  '#97B59C', // sage
  '#D4A843', // amber
  '#C97979', // rose
  '#9B79C9', // lavender
  '#C9A279', // sand
  '#79C9A2', // mint
] as const

/**
 * FNV-1a 32-bit hash — same hash family as the sigil (sigil.ts), kept for
 * deterministic spread across the ramp without clustering.
 */
function fnv1a32(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * Returns a stable hex color string for the given runId.
 *
 * The mapping is:
 *   runColor(id) === RUN_HUES[fnv1a32(id) % RUN_HUES.length]
 *
 * Collisions past 7 runs are expected and handled by the run-id text beside the
 * dot — do NOT invent out-of-palette colors to force uniqueness.
 */
export function runColor(runId: string): string {
  return RUN_HUES[fnv1a32(runId) % RUN_HUES.length]
}
